import React, { useState, useEffect } from 'react';
import { Search, LayoutGrid, ChevronDown, Menu, X } from 'lucide-react';
import { NAV_ITEMS } from '../mock/mock';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8 flex items-center justify-between h-[70px]">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#a3d859] to-[#5a8c26] flex items-center justify-center shadow-sm">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L3 7v10l9 5 9-5V7l-9-5z" />
                <path d="M12 22V12" />
                <path d="M3 7l9 5 9-5" />
              </svg>
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-[22px] font-bold text-[#2d4a1a] tracking-tight">Raynav</span>
              <span className="text-[10px] text-[#8ec045] font-bold tracking-[3px] -mt-0.5">GREEN</span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
            {NAV_ITEMS.map((item, idx) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenIdx(idx)}
                onMouseLeave={() => setOpenIdx(null)}
              >
                <button className="flex items-center gap-1 px-3 xl:px-4 py-2 text-[13px] font-bold tracking-wider text-[#333] hover:text-[#8ec045] transition-colors">
                  {item.label}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {openIdx === idx && (
                  <div className="absolute left-0 top-full pt-2 min-w-[220px] z-50">
                    <div className="bg-white shadow-xl border-t-2 border-[#8ec045] rounded-b-md py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                      {item.children.map((c) => (
                        <a
                          key={c.label}
                          href={c.href}
                          className="block px-4 py-2.5 text-[13px] text-[#444] hover:bg-[#f5f9ee] hover:text-[#8ec045] transition-colors"
                        >
                          {c.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 text-[#333] hover:text-[#8ec045] transition-colors"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>
            <button
              className="hidden md:block p-2 text-[#333] hover:text-[#8ec045] transition-colors"
              aria-label="Menu"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 text-[#333]"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Search overlay */}
        {searchOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100 px-4 lg:px-8 py-4 animate-in slide-in-from-top-2 duration-200">
            <div className="max-w-[1440px] mx-auto flex items-center gap-3">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                autoFocus
                type="text"
                placeholder="Search Raynav Green..."
                className="flex-1 outline-none text-[15px] py-2 bg-transparent"
              />
              <button onClick={() => setSearchOpen(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-white lg:hidden overflow-y-auto">
          <div className="flex items-center justify-between h-[70px] px-4 border-b">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#a3d859] to-[#5a8c26] flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L3 7v10l9 5 9-5V7l-9-5z" />
                  <path d="M12 22V12" />
                  <path d="M3 7l9 5 9-5" />
                </svg>
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-[18px] font-bold text-[#2d4a1a]">Raynav</span>
                <span className="text-[9px] text-[#8ec045] font-bold tracking-[3px] -mt-0.5">GREEN</span>
              </div>
            </div>
            <button onClick={() => setMobileOpen(false)}>
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="px-4 py-4">
            {NAV_ITEMS.map((item, idx) => (
              <details key={item.label} className="border-b border-gray-100 py-3">
                <summary className="list-none flex justify-between items-center cursor-pointer font-bold text-[14px] tracking-wide">
                  {item.label}
                  <ChevronDown className="w-4 h-4" />
                </summary>
                <div className="mt-2 pl-2">
                  {item.children.map((c) => (
                    <a key={c.label} href={c.href} className="block py-2 text-[14px] text-[#555]">
                      {c.label}
                    </a>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
